<script setup lang="ts">
import { api } from '@/api';
import { type CreateOrUpdateUnitDto } from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import UnitForm from './components/UnitForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { useUnitsStore } from '@/stores/unitsStore';
import { backendValidator } from '@/validation/validators/backendValidator';

const { t } = useTranslation();
const notificationStore = useNotificationStore();
const router = useRouter();
const unitsStore = useUnitsStore();

const initialValues: Partial<CreateOrUpdateUnitDto> = {
  name: t('Untitled'),
  isAvailable: true,
};

const { form } = useVeeForm<CreateOrUpdateUnitDto>({
  schemaName: 'CreateOrUpdateUnitDto',
  options: { initialValues },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((enumerationQuantityDto, errorHandlers) =>
      api.unitsApi.validateUnit(
        {
          createOrUpdateUnitDto: enumerationQuantityDto,
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (createOrUpdateUnitDto) => {
  const { success, result: unit } = await unitsStore.addUnit(createOrUpdateUnitDto, [
    formBackendErrorHandler(form),
  ]);

  if (success) {
    notificationStore.success(t('EntityAdded', { name: unit.name, iuid: unit.iuid }));
    await router.push({
      name: routenames.units.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('Unit_one')" :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.units.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <UnitForm />
    </BasePage>
  </form>
</template>
